// TanStack Router configuration.
// Code-based routing — routes mirror API resource structure.
// /t/:slug layout route wraps all tenant-scoped authenticated pages.

import {
  createRouter,
  createRootRoute,
  createRoute,
  redirect,
  Outlet,
} from '@tanstack/react-router'
import { TenantShell } from '@/components/shared/TenantShell'
import { LandingPage } from '@/pages/LandingPage'

import { NotFoundPage } from '@/pages/NotFoundPage'

// Framework config pages
import { TaxonomyPage } from '@/pages/framework/TaxonomyPage'
import { LikelihoodPage } from '@/pages/framework/LikelihoodPage'
import { ImpactTablesPage } from '@/pages/framework/ImpactTablesPage'
import { ScoringGridPage } from '@/pages/framework/ScoringGridPage'
import { AppetitePage } from '@/pages/framework/AppetitePage'
import { EvidenceTagsPage } from '@/pages/framework/EvidenceTagsPage'
import { AssessmentThresholdsPage } from '@/pages/framework/AssessmentThresholdsPage'
import { CategoryPage } from '@/pages/framework/CategoryPage'
import { RegisterStructurePage } from '@/pages/framework/RegisterStructurePage'

// Settings pages
import { BrandingPage } from '@/pages/settings/BrandingPage'
import { MyApiKeyPage } from '@/pages/settings/MyApiKeyPage'

// Platform pages
import { SubscriptionPage } from '@/pages/platform/SubscriptionPage'
import { FeaturesPage } from '@/pages/platform/FeaturesPage'
import { IdPSettingsPage } from '@/pages/platform/IdPSettingsPage'

// Approval policy pages
import { ApprovalPolicyPage } from '@/pages/framework/ApprovalPolicyPage'

// Admin pages (People & Access)
import { UsersPage } from '@/pages/admin/UsersPage'
import { UserDetailPage } from '@/pages/admin/UserDetailPage'
import { ApiUsersPage } from '@/pages/admin/ApiUsersPage'
import { OwnershipRolesPage } from '@/pages/admin/OwnershipRolesPage'
import { RolesPage } from '@/pages/admin/RolesPage'

// Risk pages
import { RiskLayout } from '@/pages/risks/RiskLayout'
import { RiskRegisterPage } from '@/pages/risks/RiskRegisterPage'
import { RiskDetailPage } from '@/pages/risks/RiskDetailPage'
import { RiskDetailTab } from '@/pages/risks/RiskDetailTab'
import { RiskHistoryTab } from '@/pages/risks/RiskHistoryTab'
import { RiskControlsTab } from '@/pages/risks/RiskControlsTab'
import { RiskActionsTab } from '@/pages/risks/RiskActionsTab'
import { RiskEventsTab } from '@/pages/risks/RiskEventsTab'
import { RiskChildrenTab } from '@/pages/risks/RiskChildrenTab'
import { ComingSoonTab } from '@/pages/risks/ComingSoonTab'

// Control pages
import { ControlLayout } from '@/pages/controls/ControlLayout'
import { ControlListPage } from '@/pages/controls/ControlListPage'
import { ControlDetailPage } from '@/pages/controls/ControlDetailPage'
import { ControlDetailTab } from '@/pages/controls/ControlDetailTab'
import { ControlHistoryTab } from '@/pages/controls/ControlHistoryTab'
import { ControlRisksTab } from '@/pages/controls/ControlRisksTab'
import { ControlObligationsTab } from '@/pages/controls/ControlObligationsTab'
import { ControlAssessmentsTab } from '@/pages/controls/ControlAssessmentsTab'
import { ControlEvidenceTab } from '@/pages/controls/ControlEvidenceTab'

// Action pages
import { ActionLayout } from '@/pages/actions/ActionLayout'
import { ActionListPage } from '@/pages/actions/ActionListPage'
import { ActionDetailPage } from '@/pages/actions/ActionDetailPage'
import { ActionDetailTab } from '@/pages/actions/ActionDetailTab'
import { ActionUpdatesTab } from '@/pages/actions/ActionUpdatesTab'
import { ActionControlsTab } from '@/pages/actions/ActionControlsTab'
import { ActionEventsTab } from '@/pages/actions/ActionEventsTab'
import { ActionRisksTab } from '@/pages/actions/ActionRisksTab'
import { ActionEvidenceTab } from '@/pages/actions/ActionEvidenceTab'
import { ActionHistoryTab } from '@/pages/actions/ActionHistoryTab'

// Obligation pages
import { ObligationLayout } from '@/pages/obligations/ObligationLayout'
import { ObligationListPage } from '@/pages/obligations/ObligationListPage'
import { ObligationDetailPage } from '@/pages/obligations/ObligationDetailPage'

// Reporting pages
import { ReportingLayout } from '@/pages/reporting/ReportingLayout'
import { RiskHeatmapPage } from '@/pages/reporting/RiskHeatmapPage'
import { EventsLastPeriodPage } from '@/pages/reporting/EventsLastPeriodPage'
import { ControlAssessmentsPage } from '@/pages/reporting/ControlAssessmentsPage'
import { ControlsSummaryPage } from '@/pages/reporting/ControlsSummaryPage'

// Import pages
import { ImportPage } from '@/pages/import/ImportPage'

// Approval page (email-based approval links)
import { ApprovalPage } from '@/pages/ApprovalPage'

// Event pages
import { EventLayout } from '@/pages/events/EventLayout'
import { EventListPage } from '@/pages/events/EventListPage'
import { EventDetailPage } from '@/pages/events/EventDetailPage'
import { EventDetailTab } from '@/pages/events/EventDetailTab'
import { EventRisksTab } from '@/pages/events/EventRisksTab'
import { EventActionsTab } from '@/pages/events/EventActionsTab'
import { EventEvidenceTab } from '@/pages/events/EventEvidenceTab'

// --- Root route ---
const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: NotFoundPage,
})

// --- Landing page ---
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
})

// --- Tenant layout route ---
// TenantShell handles OIDC init, auth, and provides the layout shell
const tenantLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/t/$slug',
  component: TenantShell,
})

// --- Tenant index redirect → risks ---
const tenantIndexRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/t/$slug/risks', params })
  },
})

// --- Risk routes ---

// /t/:slug/risks — persistent tree layout with right panel content via Outlet
const risksRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/risks',
  component: RiskLayout,
})

const risksIndexRoute = createRoute({
  getParentRoute: () => risksRoute,
  path: '/',
  component: RiskRegisterPage,
})

// /t/:slug/risks/:riskId — T2 Detail + Tabs
const riskDetailRoute = createRoute({
  getParentRoute: () => risksRoute,
  path: '/$riskId',
  component: RiskDetailPage,
})

// Risk detail sub-routes (tabs)
const riskDetailIndexRoute = createRoute({
  getParentRoute: () => riskDetailRoute,
  path: '/',
  component: RiskDetailTab,
})

const riskControlsTabRoute = createRoute({
  getParentRoute: () => riskDetailRoute,
  path: '/controls',
  component: RiskControlsTab,
})

const riskActionsTabRoute = createRoute({
  getParentRoute: () => riskDetailRoute,
  path: '/actions',
  component: RiskActionsTab,
})

const riskEventsTabRoute = createRoute({
  getParentRoute: () => riskDetailRoute,
  path: '/events',
  component: RiskEventsTab,
})

const riskChildrenTabRoute = createRoute({
  getParentRoute: () => riskDetailRoute,
  path: '/children',
  component: RiskChildrenTab,
})

const riskHistoryTabRoute = createRoute({
  getParentRoute: () => riskDetailRoute,
  path: '/history',
  component: RiskHistoryTab,
})

// --- Control routes ---

// /t/:slug/controls — persistent tree layout with right panel content via Outlet
const controlsRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/controls',
  component: ControlLayout,
})

const controlsIndexRoute = createRoute({
  getParentRoute: () => controlsRoute,
  path: '/',
  component: ControlListPage,
})

// /t/:slug/controls/:controlId — T2 Detail + Tabs
const controlDetailRoute = createRoute({
  getParentRoute: () => controlsRoute,
  path: '/$controlId',
  component: ControlDetailPage,
})

// Control detail sub-routes (tabs)
const controlDetailIndexRoute = createRoute({
  getParentRoute: () => controlDetailRoute,
  path: '/',
  component: ControlDetailTab,
})

const controlAssessmentsTabRoute = createRoute({
  getParentRoute: () => controlDetailRoute,
  path: '/assessments',
  component: ControlAssessmentsTab,
})

const controlKpisTabRoute = createRoute({
  getParentRoute: () => controlDetailRoute,
  path: '/kpis',
  component: () => ComingSoonTab({ title: 'Key Performance Indicators' }),
})

const controlRisksTabRoute = createRoute({
  getParentRoute: () => controlDetailRoute,
  path: '/risks',
  component: ControlRisksTab,
})

const controlObligationsTabRoute = createRoute({
  getParentRoute: () => controlDetailRoute,
  path: '/obligations',
  component: ControlObligationsTab,
})

const controlEvidenceTabRoute = createRoute({
  getParentRoute: () => controlDetailRoute,
  path: '/evidence',
  component: ControlEvidenceTab,
})

const controlHistoryTabRoute = createRoute({
  getParentRoute: () => controlDetailRoute,
  path: '/history',
  component: ControlHistoryTab,
})

// --- Obligation routes ---

// /t/:slug/obligations — persistent two-panel layout with category tree + Outlet
const obligationsRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/obligations',
  component: ObligationLayout,
})

const obligationsIndexRoute = createRoute({
  getParentRoute: () => obligationsRoute,
  path: '/',
  component: ObligationListPage,
})

// /t/:slug/obligations/:frameworkId — detail with nested obligation tree + item detail
const obligationDetailRoute = createRoute({
  getParentRoute: () => obligationsRoute,
  path: '/$frameworkId',
  component: ObligationDetailPage,
})

// --- Action routes ---

// /t/:slug/actions — persistent tree layout with right panel content via Outlet
const actionsRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/actions',
  component: ActionLayout,
})

const actionsIndexRoute = createRoute({
  getParentRoute: () => actionsRoute,
  path: '/',
  component: ActionListPage,
})

// /t/:slug/actions/:actionId — T2 Detail + Tabs
const actionDetailRoute = createRoute({
  getParentRoute: () => actionsRoute,
  path: '/$actionId',
  component: ActionDetailPage,
})

// Action detail sub-routes (tabs)
const actionDetailIndexRoute = createRoute({
  getParentRoute: () => actionDetailRoute,
  path: '/',
  component: ActionDetailTab,
})

const actionUpdatesTabRoute = createRoute({
  getParentRoute: () => actionDetailRoute,
  path: '/updates',
  component: ActionUpdatesTab,
})

const actionControlsTabRoute = createRoute({
  getParentRoute: () => actionDetailRoute,
  path: '/controls',
  component: ActionControlsTab,
})

const actionEventsTabRoute = createRoute({
  getParentRoute: () => actionDetailRoute,
  path: '/events',
  component: ActionEventsTab,
})

const actionRisksTabRoute = createRoute({
  getParentRoute: () => actionDetailRoute,
  path: '/risks',
  component: ActionRisksTab,
})

const actionEvidenceTabRoute = createRoute({
  getParentRoute: () => actionDetailRoute,
  path: '/evidence',
  component: ActionEvidenceTab,
})

const actionHistoryTabRoute = createRoute({
  getParentRoute: () => actionDetailRoute,
  path: '/history',
  component: ActionHistoryTab,
})

// --- Event routes ---

// /t/:slug/events — persistent tree layout with right panel content via Outlet
const eventsRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/events',
  component: EventLayout,
})

const eventsIndexRoute = createRoute({
  getParentRoute: () => eventsRoute,
  path: '/',
  component: EventListPage,
})

// /t/:slug/events/:eventId — T2 Detail + Tabs
const eventDetailRoute = createRoute({
  getParentRoute: () => eventsRoute,
  path: '/$eventId',
  component: EventDetailPage,
})

// Event detail sub-routes (tabs)
const eventDetailIndexRoute = createRoute({
  getParentRoute: () => eventDetailRoute,
  path: '/',
  component: EventDetailTab,
})

const eventRisksTabRoute = createRoute({
  getParentRoute: () => eventDetailRoute,
  path: '/risks',
  component: EventRisksTab,
})

const eventActionsTabRoute = createRoute({
  getParentRoute: () => eventDetailRoute,
  path: '/actions',
  component: EventActionsTab,
})

const eventEvidenceTabRoute = createRoute({
  getParentRoute: () => eventDetailRoute,
  path: '/evidence',
  component: EventEvidenceTab,
})

const eventHistoryTabRoute = createRoute({
  getParentRoute: () => eventDetailRoute,
  path: '/history',
  component: () => ComingSoonTab({ title: 'Audit Log' }),
})

// --- Reporting routes ---
const reportingRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/reporting',
  component: ReportingLayout,
})

const reportingIndexRoute = createRoute({
  getParentRoute: () => reportingRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/t/$slug/reporting/risk-heatmap', params })
  },
})

const riskHeatmapRoute = createRoute({
  getParentRoute: () => reportingRoute,
  path: '/risk-heatmap',
  component: RiskHeatmapPage,
})

const eventsLastPeriodRoute = createRoute({
  getParentRoute: () => reportingRoute,
  path: '/events-last-period',
  component: EventsLastPeriodPage,
})

const controlAssessmentsRoute = createRoute({
  getParentRoute: () => reportingRoute,
  path: '/control-assessments',
  component: ControlAssessmentsPage,
})

const controlsSummaryRoute = createRoute({
  getParentRoute: () => reportingRoute,
  path: '/controls-summary',
  component: ControlsSummaryPage,
})

// --- Framework config routes ---
const frameworkRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/framework',
  component: Outlet,
})

const frameworkIndexRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/t/$slug/framework/taxonomy', params })
  },
})

const taxonomyRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/taxonomy',
  component: TaxonomyPage,
})

const likelihoodRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/likelihood',
  component: LikelihoodPage,
})

const impactTablesRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/impact-tables',
  component: ImpactTablesPage,
})

const scoringGridRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/scoring-grid',
  component: ScoringGridPage,
})

const appetiteRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/appetite',
  component: AppetitePage,
})

const evidenceTagsRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/evidence-tags',
  component: EvidenceTagsPage,
})

const assessmentThresholdsRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/assessment-thresholds',
  component: AssessmentThresholdsPage,
})

// --- Category structure routes (one per category type) ---
const controlCategoriesRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/categories/control',
  component: () => CategoryPage({ categoryType: 'control' }),
})

const eventCategoriesRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/categories/event',
  component: () => CategoryPage({ categoryType: 'event' }),
})

const actionCategoriesRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/categories/action',
  component: () => CategoryPage({ categoryType: 'action' }),
})

const frameworkCategoriesRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/categories/framework',
  component: () => CategoryPage({ categoryType: 'framework' }),
})

const registerStructureRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/registers',
  component: RegisterStructurePage,
})

// --- Approval policy routes (under /framework/approvals) ---
const approvalControlsRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/approvals/controls',
  component: () =>
    ApprovalPolicyPage({
      nodeType: 'control_category',
      policyType: 'independent_assessment',
    }),
})

const approvalActionsRoute = createRoute({
  getParentRoute: () => frameworkRoute,
  path: '/approvals/actions',
  component: () =>
    ApprovalPolicyPage({
      nodeType: 'action_category',
      policyType: 'closure_approval',
    }),
})

// --- Admin routes (People & Access) ---
const adminRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/admin',
  component: Outlet,
})

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/t/$slug/admin/users', params })
  },
})

const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/users',
  component: UsersPage,
})

const adminUserDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/users/$userId',
  component: UserDetailPage,
})

const adminOwnershipRolesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/ownership-roles',
  component: OwnershipRolesPage,
})

const adminRolesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/roles',
  component: RolesPage,
})

const adminApiUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/api-users',
  component: ApiUsersPage,
})

// --- Import routes ---
const importRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/import',
  component: () => ImportPage({}),
})

const importDetailRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/import/$importId',
  component: function ImportDetailWrapper() {
    const { importId } = importDetailRoute.useParams()
    return ImportPage({ importId })
  },
})

// --- Platform routes (admin-only) ---
const platformRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/platform',
  component: Outlet,
})

const platformIndexRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/t/$slug/platform/subscription', params })
  },
})

const platformSubscriptionRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/subscription',
  component: SubscriptionPage,
})

const platformFeaturesRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/features',
  component: FeaturesPage,
})

const platformAuthenticationRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/authentication',
  component: IdPSettingsPage,
})

// --- Settings routes ---
const settingsRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/settings',
  component: Outlet,
})

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/t/$slug/settings/api-key', params })
  },
})

const brandingRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/branding',
  component: BrandingPage,
})

const myApiKeyRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/api-key',
  component: MyApiKeyPage,
})

// --- Approval route (email links: /t/:slug/approve?token=...) ---
const approveRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/approve',
  component: ApprovalPage,
})

// --- OIDC callback route ---
// This route exists so the OIDC redirect_uri resolves to a valid route.
// The actual redirect to dashboard is handled by onSigninCallback in TenantShell.
const authCallbackRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: '/auth/callback',
  component: () => null,
})

// --- Build route tree ---
const routeTree = rootRoute.addChildren([
  landingRoute,
  tenantLayoutRoute.addChildren([
    tenantIndexRoute,
    risksRoute.addChildren([
      risksIndexRoute,
      riskDetailRoute.addChildren([
        riskDetailIndexRoute,
        riskControlsTabRoute,
        riskActionsTabRoute,
        riskEventsTabRoute,
        riskChildrenTabRoute,
        riskHistoryTabRoute,
      ]),
    ]),
    controlsRoute.addChildren([
      controlsIndexRoute,
      controlDetailRoute.addChildren([
        controlDetailIndexRoute,
        controlRisksTabRoute,
        controlObligationsTabRoute,
        controlAssessmentsTabRoute,
        controlKpisTabRoute,
        controlEvidenceTabRoute,
        controlHistoryTabRoute,
      ]),
    ]),
    obligationsRoute.addChildren([
      obligationsIndexRoute,
      obligationDetailRoute,
    ]),
    actionsRoute.addChildren([
      actionsIndexRoute,
      actionDetailRoute.addChildren([
        actionDetailIndexRoute,
        actionUpdatesTabRoute,
        actionControlsTabRoute,
        actionEventsTabRoute,
        actionRisksTabRoute,
        actionEvidenceTabRoute,
        actionHistoryTabRoute,
      ]),
    ]),
    eventsRoute.addChildren([
      eventsIndexRoute,
      eventDetailRoute.addChildren([
        eventDetailIndexRoute,
        eventRisksTabRoute,
        eventActionsTabRoute,
        eventEvidenceTabRoute,
        eventHistoryTabRoute,
      ]),
    ]),
    reportingRoute.addChildren([
      reportingIndexRoute,
      riskHeatmapRoute,
      eventsLastPeriodRoute,
      controlAssessmentsRoute,
      controlsSummaryRoute,
    ]),
    frameworkRoute.addChildren([
      frameworkIndexRoute,
      taxonomyRoute,
      likelihoodRoute,
      impactTablesRoute,
      scoringGridRoute,
      appetiteRoute,
      evidenceTagsRoute,
      assessmentThresholdsRoute,
      controlCategoriesRoute,
      eventCategoriesRoute,
      actionCategoriesRoute,
      frameworkCategoriesRoute,
      registerStructureRoute,
      approvalControlsRoute,
      approvalActionsRoute,
    ]),
    adminRoute.addChildren([
      adminIndexRoute,
      adminUsersRoute,
      adminUserDetailRoute,
      adminApiUsersRoute,
      adminOwnershipRolesRoute,
      adminRolesRoute,
    ]),
    platformRoute.addChildren([
      platformIndexRoute,
      platformSubscriptionRoute,
      platformFeaturesRoute,
      platformAuthenticationRoute,
    ]),
    settingsRoute.addChildren([settingsIndexRoute, brandingRoute, myApiKeyRoute]),
    importRoute,
    importDetailRoute,
    approveRoute,
    authCallbackRoute,
  ]),
])

// --- Create router ---
export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundPage,
})

// --- Type registration for type-safe navigation ---
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

